import * as Icons from 'lucide-react';
import { Slash, type LucideIcon } from 'lucide-react';
import { Pill, cn } from '@cyphex/ui';
import type { SlashCommand } from '@cyphex/types/platform';

export interface SlashCommandMenuProps {
  commands: readonly SlashCommand[];
  activeIndex: number;
  onPick: (command: SlashCommand) => void;
  /**
   * Viewport rect of the typed token.
   *
   * Supplied by the CHANNEL composer, whose editor is a contentEditable inside
   * a card that is not a positioning context — the menu has to be anchored to
   * the caret in viewport coordinates, exactly like the mention menu beside it.
   * Omitted by the chat composer, which owns a `relative` shell and opens the
   * menu upward from it.
   */
  anchor?: DOMRect;
  className?: string;
}

const ANCHOR_OFFSET_PX = 6;
const ANCHOR_WIDTH_PX = 384;

/**
 * The composer's `/` popover.
 *
 * Rendered ABOVE the composer, not below: the composer already sits at the
 * bottom of the viewport, so a menu underneath it would open off-screen.
 *
 * Keyboard ownership lives in the composer, not here — this component renders
 * `activeIndex` and reports clicks. Splitting it the other way would mean the
 * menu needs a ref to the textarea to know when to give the keys back.
 */
export function SlashCommandMenu({
  commands,
  activeIndex,
  onPick,
  anchor,
  className,
}: SlashCommandMenuProps) {
  if (commands.length === 0) return null;

  // Anchored mode opens BELOW the caret. The channel composer sits near the top
  // of a scrolling feed, so upward is where the menu would be clipped.
  const anchored = anchor === undefined ? undefined : {
    top: anchor.bottom + ANCHOR_OFFSET_PX,
    left: Math.max(
      ANCHOR_OFFSET_PX,
      Math.min(anchor.left, window.innerWidth - ANCHOR_WIDTH_PX - ANCHOR_OFFSET_PX * 2),
    ),
    width: ANCHOR_WIDTH_PX,
  };

  return (
    <div
      role="listbox"
      aria-label="Slash commands"
      {...(anchored === undefined ? {} : { style: anchored })}
      className={cn(
        'z-dropdown overflow-hidden rounded-lg border border-border bg-popover shadow-dropdown',
        anchored === undefined
          ? 'absolute bottom-full left-0 mb-2 w-full max-w-md'
          : 'fixed animate-scale-in',
        className,
      )}
    >
      <p className="border-b border-border px-3 py-1.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
        Commands
      </p>
      <ul className="max-h-64 overflow-y-auto py-1">
        {commands.map((command, index) => {
          const Glyph = iconFor(command.icon);
          const active = index === activeIndex;
          return (
            <li key={command.id ?? command.command}>
              <button
                type="button"
                role="option"
                aria-selected={active}
                // `onMouseDown` with preventDefault, never `onClick`: a click
                // blurs the textarea first, and a blurred composer loses the
                // caret position the inserted text has to land at.
                onMouseDown={(event) => {
                  event.preventDefault();
                  onPick(command);
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors duration-fast',
                  active ? 'bg-primary-subtle' : 'hover:bg-subtle',
                )}
              >
                <Glyph size={16} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-1.5">
                    <code className="font-mono text-md font-semibold text-foreground">
                      /{command.command}
                    </code>
                    {command.usageHint === null ? null : (
                      <code className="truncate font-mono text-sm text-muted-foreground">
                        {command.usageHint}
                      </code>
                    )}
                  </span>
                  <span className="block truncate text-sm text-muted-foreground">
                    {command.description}
                  </span>
                </span>
                {command.appName === null || command.appName === undefined ? null : (
                  <Pill tone="brand" size="sm">
                    {command.appName}
                  </Pill>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Resolve a lucide icon by name, with a slash as the fallback.
 *
 * The name is TENANT DATA — an administrator typed it into a form — so it is
 * looked up rather than imported, and anything that is not a real icon
 * component degrades to the default instead of crashing the composer. A bad
 * icon name must never be able to take the message box down.
 */
function iconFor(name: string | null): LucideIcon {
  if (name === null) return Slash;
  const candidate = (Icons as unknown as Record<string, unknown>)[name];
  return typeof candidate === 'function' || typeof candidate === 'object'
    ? (candidate as LucideIcon)
    : Slash;
}
